import { ColumnsType } from 'antd/es/table';
import { Moment } from 'moment';
import React, { FC, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { User } from 'api/models';
import { Project } from 'api/project';
import { Category } from 'api/timesheets/categories';
import {
  EnabledSourcesConfiguration,
  TimesheetApprovalType,
} from 'api/timesheets/configuration/configuration';
import { GetTimeOffTooltipContent } from 'components/atoms/time-off-tooltip';
import { ProjectNameCell } from 'components/organisms/timesheets/common/cells/project-name-cell';
import { TaskNameCell } from 'components/organisms/timesheets/common/cells/task-name-cell';
import { TotalTimeCell } from 'components/organisms/timesheets/common/cells/time-cells';
import {
  isRowDeleted,
  RowWithTooltip,
} from 'components/organisms/timesheets/common/table-row';
import {
  checkTimeEntryConfig,
  getRowKey,
  showTaskColumn,
} from 'components/organisms/timesheets/common/time-entry-utils';
import {
  DateColumnHeaderCell,
  TimesheetTable,
} from 'components/organisms/timesheets/common/timesheet-table';
import {
  getColumnClassesForTimeCell,
  getDays as getSevenDays,
  getTotalMinutesForTask,
} from 'components/organisms/timesheets/common/utils';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { Categories } from 'components/pages/timesheet-settings/categories';
import { fullName } from 'utils/full-name';
import { checkIfCurrentDateHasTimeOff } from 'utils/time-off-utils';
import { EntryDetailDrawer } from '../drawers';
import {
  TimesheetStatus,
  TimesheetTableTimeEntry,
  TimesheetWeeklyRow,
  TimesheetWeeklyRowTask,
} from '../types';
import { EditableCell } from '../week-view-table/editable-cell';
import { ActionBtnCell } from './action-btn-cell';
import { ActionCell } from './action-cell';

const Action = styled(ActionCell)``;

const Table = styled(TimesheetTable)`
  .ant-table-tbody > tr > td {
    vertical-align: middle;
    position: relative;
  }
  .ant-table-tbody > tr > td.hoverable-cell {
    cursor: pointer;
    &:hover {
      background-color: var(--blue-10);
    }
  }
  .ant-table-tbody > tr > td.holiday-cell.hoverable-cell {
    &:hover {
      background: repeating-linear-gradient(
        -47deg,
        #fff 1%,
        #fff 2%,
        var(--coolest-gray-50) 3%,
        #fff 4%,
        #fff 5%
      );
    }
  }
  ${Action} {
    display: none;
  }
  .ant-table-tbody > tr:hover {
    ${Action} {
      display: inline-block;
    }
  }
`;

const TaskWrapperCell = styled.div`
  border-right: 1px solid var(--table-border-color);
`;

const getColumnConfig = ({
  startDate,
  timeEntryCategories,
  editable,
  workingDays,
  updateTimeEntryInTask,
  deleteTimeEntryInTask,
  addTimeEntryInTask,
  showEntryDetail,
  activeColumn,
  setActiveColumn,
  user,
  timesheetEntriesConfig,
  projects,
  data,
  isProjectOwnerApproval,
  isMangerApproval,
  isWeekFreezed,
  isUserAdmin,
  mandateCategory,
  approveTask,
  rejectTask,
  withdrawTask,
}: {
  startDate: Moment;
  timeEntryCategories: Category[];
  workingDays: WorkingDaysConfiguration;
  editable: boolean;
  updateTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntries: TimesheetTableTimeEntry,
  ) => void;
  deleteTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntryId: number,
  ) => void;
  addTimeEntryInTask: (task: TimesheetWeeklyRowTask, entryAt: string) => void;
  showEntryDetail: (timesheet: TimesheetWeeklyRow, day?: string) => void;
  activeColumn?: string;
  setActiveColumn?: (value: string | undefined) => void;
  projects: Project[] | undefined;
  timesheetEntriesConfig: EnabledSourcesConfiguration;
  data: TimesheetWeeklyRow[];
  user: User;
  isProjectOwnerApproval: boolean;
  isMangerApproval: boolean;
  isWeekFreezed: boolean;
  isUserAdmin: boolean;
  mandateCategory: boolean;
  approveTask: (task: TimesheetWeeklyRowTask, projectId?: number) => void;
  rejectTask: (
    task: TimesheetWeeklyRowTask,
    content?: string,
    projectId?: number,
  ) => Promise<any>;
  withdrawTask: (task: TimesheetWeeklyRowTask, projectId?: number) => void;
}): ColumnsType<any> => {
  const { isOnlyProjectsEnabled } = checkTimeEntryConfig(
    timesheetEntriesConfig,
  );

  const dayColumns = getSevenDays(startDate).map((day) => {
    const isTimeOff = checkIfCurrentDateHasTimeOff(
      user?.userTimeOffDetails,
      day,
    );
    let timeOffTooltipContent: any = '';

    if (isTimeOff) {
      timeOffTooltipContent = (
        <GetTimeOffTooltipContent
          timeOff={isTimeOff}
          userName={fullName(user)}
        />
      );
    }

    return {
      title: <DateColumnHeaderCell date={day} user={user} />,
      key: day.format('DD-MM-YYYY'),
      type: 'time-entry-cell',
      width: '8%',
      className: `${getColumnClassesForTimeCell(
        day,
        workingDays,
        activeColumn,
        user,
      )} hoverable-cell`,
      onCell: (record) => {
        const currentDayData = record.days[day.format('YYYY-MM-DD')];

        return {
          title: '',
          type: 'time-entry-cell',
          timeEntries: currentDayData,
          task: record.task,
          days: record.days,
          day,
          timeEntryCategories,
          mandateCategory,
          canEditTimeCell:
            editable &&
            (isMangerApproval
              ? true
              : record.task?.approvalStatus !== TimesheetStatus.Approved),
          timesheetEntriesConfig,
          updateDayEntry: (
            entryAt: string,
            timeEntry: TimesheetTableTimeEntry,
          ) => {
            updateTimeEntryInTask(record.task, entryAt, timeEntry);
          },
          deleteDayEntry: (entryAt: string, timeEntryId: number) => {
            deleteTimeEntryInTask(record.task, entryAt, timeEntryId);
          },
          addDayEntry: (entryAt: string) => {
            addTimeEntryInTask(record.task, entryAt);
          },
          onMouseEnter: () => {
            setActiveColumn?.(day.format('YYYY-MM-DD'));
          },
          onMouseLeave: () => {
            setActiveColumn?.(undefined);
          },
          timeOffTooltipContent,
        };
      },
    };
  });

  return [
    isMangerApproval
      ? {
          title: (
            <strong>
              <FormattedMessage id="projects.title" />
            </strong>
          ),
          ellipsis: true,
          key: 'projectName',
          width: showTaskColumn(isOnlyProjectsEnabled, data) ? '16%' : '32%',
          render: (_, record: TimesheetWeeklyRow) => (
            <ProjectNameCell
              task={record.task}
              days={record.days}
              projects={projects}
            />
          ),
        }
      : {},
    showTaskColumn(isOnlyProjectsEnabled, data)
      ? {
          title: <FormattedMessage id="work" />,
          key: 'tasks',
          width: isMangerApproval ? '16%' : '20%',
          render: (_, record: TimesheetWeeklyRow) => {
            return (
              <TaskWrapperCell>
                <TaskNameCell
                  task={record.task}
                  days={record.days}
                  projects={projects}
                  timesheetEntriesConfig={timesheetEntriesConfig}
                  isProjectOwnerApproval={isProjectOwnerApproval}
                />
              </TaskWrapperCell>
            );
          },
        }
      : {},
    ...dayColumns,
    {
      title: <FormattedMessage id="timesheets.column.total" />,
      key: 'total_time',
      align: 'left',
      width: '8%',
      render: (_, record: TimesheetWeeklyRow) => (
        <TotalTimeCell minutes={getTotalMinutesForTask(record.days)} />
      ),
    },
    {
      title: '',
      key: 'actions',
      width: '4%',
      align: 'right',
      render: (_, record: TimesheetWeeklyRow) => (
        <Action showDetail={() => showEntryDetail(record)} />
      ),
    },
    !isMangerApproval
      ? {
          title: '',
          key: 'actions_btns',
          width: '12%',
          align: 'right',
          render: (_, record: TimesheetWeeklyRow) => (
            <ActionBtnCell
              record={record}
              approveTask={approveTask}
              rejectTask={rejectTask}
              withdrawTask={withdrawTask}
              isWeekFreezed={isWeekFreezed}
              isUserAdmin={isUserAdmin}
            />
          ),
        }
      : {},
  ];
};

interface Props {
  startDate: Moment;
  timeEntryCategories: Category[];
  editable: boolean;
  workingDays: WorkingDaysConfiguration;
  approveTask: (task: TimesheetWeeklyRowTask, projectId?: number) => void;
  rejectTask: (
    task: TimesheetWeeklyRowTask,
    content?: string,
    projectId?: number,
  ) => Promise<any>;
  withdrawTask: (task: TimesheetWeeklyRowTask, projectId?: number) => void;
  updateTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntries: TimesheetTableTimeEntry,
  ) => void;
  deleteTimeEntryInTask: (
    task: TimesheetWeeklyRowTask,
    entryAt: string,
    timeEntryId: number,
  ) => void;
  addTimeEntryInTask: (task: TimesheetWeeklyRowTask, entryAt: string) => void;
  timesheetRowData: TimesheetWeeklyRow[];
  categories: Categories;
  projects: Project[] | undefined;
  timesheetEntriesConfig: EnabledSourcesConfiguration;
  user: User;
  approvalSetting: TimesheetApprovalType;
  isWeekFreezed: boolean;
  isUserAdmin: boolean;
  mandateCategory: boolean;
}

export const SingleUserViewTable: FC<Props> = ({
  startDate,
  timeEntryCategories,
  editable,
  workingDays,
  approveTask,
  rejectTask,
  withdrawTask,
  updateTimeEntryInTask,
  deleteTimeEntryInTask,
  addTimeEntryInTask,
  timesheetRowData,
  categories,
  user,
  timesheetEntriesConfig,
  projects,
  approvalSetting,
  isWeekFreezed,
  isUserAdmin,
  mandateCategory,
}) => {
  const [selectedTaskRow, setSelectedTaskRow] =
    useState<[TimesheetWeeklyRow, string | undefined]>();

  const [activeColumn, setActiveColumn] = useState<string>();

  const components = {
    body: {
      row: RowWithTooltip,
      cell: EditableCell,
    },
  };

  const isMangerApproval = useMemo(
    () => approvalSetting === TimesheetApprovalType.Manager,
    [approvalSetting],
  );

  const isProjectOwnerApproval = useMemo(
    () => approvalSetting === TimesheetApprovalType.ProjectOwner,
    [approvalSetting],
  );

  const columns = getColumnConfig({
    startDate,
    timeEntryCategories,
    editable,
    workingDays,
    updateTimeEntryInTask,
    deleteTimeEntryInTask,
    addTimeEntryInTask,
    showEntryDetail: (record, day) => {
      setSelectedTaskRow([record, day]);
    },
    activeColumn,
    setActiveColumn,
    user,
    timesheetEntriesConfig,
    projects,
    data: timesheetRowData,
    isProjectOwnerApproval,
    isMangerApproval,
    isWeekFreezed,
    isUserAdmin,
    approveTask,
    rejectTask,
    withdrawTask,
    mandateCategory,
  });

  const PAGE_HEADER_HEIGHT = 114;
  const ACTION_BAR_HEIGHT = 48;
  const TABLE_HEADER_HEIGHT = 48;
  const TABLE_SUMMARY_HEIGHT = 32;
  const FOOTER_ACTION_ROW = 56;
  const TABLE_BODY_HEIGHT =
    PAGE_HEADER_HEIGHT +
    ACTION_BAR_HEIGHT +
    TABLE_HEADER_HEIGHT +
    TABLE_SUMMARY_HEIGHT +
    FOOTER_ACTION_ROW;

  const USER_TIMESHEET_CAPACITY_WIDGET = 90;

  const TOTAL_HEIGHT = isMangerApproval
    ? TABLE_BODY_HEIGHT + USER_TIMESHEET_CAPACITY_WIDGET
    : TABLE_BODY_HEIGHT;

  return (
    <>
      <Table
        columns={columns}
        components={components}
        dataSource={timesheetRowData}
        pagination={false}
        sticky
        rowKey={(row, index) => {
          const record = row as TimesheetWeeklyRow;

          return getRowKey(record.task, index);
        }}
        rowClassName={(props: any) => {
          let classes = '';

          if (isRowDeleted(props.task).isDeleted) {
            classes = classes.concat('in-active');
          }

          return classes;
        }}
        onRow={() =>
          ({ tableType: 'approver-table' } as React.HTMLAttributes<HTMLElement>)
        }
        scroll={{ y: `calc(100vh - ${TOTAL_HEIGHT}px)` }}
      />
      <EntryDetailDrawer
        startDate={startDate}
        onClose={() => setSelectedTaskRow(undefined)}
        timesheet={selectedTaskRow?.[0]}
        selectedDay={selectedTaskRow?.[1]}
        categories={categories}
        user={user}
      />
    </>
  );
};
